import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, untracked } from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { distinctUntilChanged, filter, from, map, Subscription, switchMap } from "rxjs";
import { IBrandCard, IOfferCard, IPackage } from "../../types";
import { ProfileService } from "../profile/profile.service";
import { backend } from "../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { MetaService } from "../../meta.service";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";

@Component({
  selector: "coupon-checkout-confirm",
  templateUrl: "./coupon-checkout-confirm.component.html",
  styleUrls: ["./coupon-checkout-confirm.component.scss"],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink]
})
export default class CouponCheckoutConfirmComponent {
  profile = inject(ProfileService);
  http = inject(HttpClient);
  activated = inject(ActivatedRoute);
  meta = inject(MetaService);
  destroy = inject(DestroyRef);

  plan = toSignal(this.activated.queryParams.pipe(map(params => params.plan), distinctUntilChanged()));
  code = toSignal(this.activated.queryParams.pipe(map(params => params.code), distinctUntilChanged()));

  /// Stores the package previously requested
  checkPkg: string = "";
  plans: string[] = [];
  offers: { [key: string]: IOfferCard[] } = {
    malta: [],
    gozo: [],
    comino: [],
    activities: [],
    valletta: [],
  };
  fashionBrands: IBrandCard[] = [];
  foodBrands: IBrandCard[] = [];
  activitiesPlans: string[] = [];
  title: string = "Traveller Discounts";
  text: string = "Available on the Traveller Plan";
  subtext: string =
    "Access to Exclusive Discounts on Excursions, Attractions, Restaurants & Fashion Brands";
  subscription: Subscription | null = null
  subtitle: string = "Book your adventures in Malta with cloudigo from now";
  bannerTitle: string = "Save money on your adventures in Malta and Gozo";
  bannerImg: string = "old/tourist/welcome_banner.png";
  smBannerImg: string = "old/tourist/welcome_banner_small.png";

  constructor() {
    // this.activated.params.subscribe((p) => {
    //   const from_home = !!p.from_home;

    //   if (from_home) {
    //     requestAnimationFrame(() => {
    //       this.ss.location.replaceState("/");
    //     });
    //   }
    // });

    effect(() => {
      const plan = this.plan();

      untracked(() => {
        if(plan){
          this.http.get<IPackage>(backend + 'package/info', { params: { slug: plan, retail: true } }).subscribe((pkg) => this.setPackage(pkg));
        }
      });
    });

    effect(() => {
      const code = this.code();

      untracked(() => {
        if(code){
          from(this.profile.auth.fireAuth.authStateReady()).pipe(
            filter(() => !!this.profile.auth.userSession()),
            switchMap(() => this.profile.addCoupon(code))
          ).pipe(takeUntilDestroyed(this.destroy)).subscribe({
            next: (value) => {
              if (value.next_action?.action?.includes('sync')) {
                this.profile.auth.sync();
              }
            },
            error: ({ error = {} }) => {
              console.error(error);
              this.profile.auth.sync();
            },
          });
        }
      });
    });
  }

  setPackage(pkg: IPackage) {
    if (pkg && pkg.plans) {
      this.plans = pkg.plans.map((p) => p.toString());
      this.alterDisplay(pkg.slug);
    }
  }

  alterDisplay(pkgName: string) {
    // If details haven't been repetitevly brought for the same package
    if (pkgName != this.checkPkg) {
      this.checkPkg = pkgName;
      this.plans = this.plans ?? [];
      // Find at least one element which the value includes "tourist_activities"
      this.activitiesPlans = this.plans.filter((p) =>
        p.includes("tourist_activities")
      );
      this.subtext =
        this.activitiesPlans.length > 0
          ? this.subtext
          : "Access to Exclusive Discounts on Attractions, Restaurants & Fashion Brands";
      this.getFashionBrands();
      this.getFeaturedOffers();
      this.getFoodBrands();
      this.getActivities();
    }
  }

  getFashionBrands() {
    this.http.get<IBrandCard[] | { brands: IBrandCard[] }>(backend + 'v3/brands/featured', {
      params: {
        filter: "5b3e193de7179a1ccf3cbe80",
        sort: 'popularity',
        limit: 999,
        plans: this.plans.filter((p) => p != "global").join(',')
      }
    }).subscribe({
      next: (value) => {
        if('brands' in value){
          this.fashionBrands = value.brands;
        } else {
          this.fashionBrands = value;
        }
      },
      error: (error) => {
        console.warn(error);
      },
    });
  }

  getActivities() {
    this.http.get<IOfferCard[]>(backend + 'v3/offers/featured', {
      params: {
        filter: "!5b3e190bbbe26a4ac441997f,!5b3e193de7179a1ccf3cbe80",
        sort: 'popularity',
        limit: 300,
        plans: this.plans.filter((p) => p != "global").join(','),
        brands: [
          "612f71806ed6b6e31e6b9efe",
          "62629f6e17ea7e47e6a35536",
          "627e0e8a8dbffef8bbef8b5e",
          "5dbc4b386850cc72794d7de3",
          "61e557b4c5fd043ee413ac20",
          "62ac942fc3420545653759df",
        ].join(',')
      }
    }).subscribe({
      next: (activities) => {
        this.offers["activities"] = activities;
      },
      error: (error) => console.warn(error),
    });
  }

  getFeaturedOffers() {
    this.http.get<IOfferCard[]>(backend + 'v3/offers/featured', {
      params: {
        filter: "!5b3e190bbbe26a4ac441997f,!5b3e193de7179a1ccf3cbe80",
        sort: 'popularity',
        limit: 300,
        plans: this.plans.filter((p) => p != "global").join(',')
      }
    }).subscribe({
      next: (excursions) => {
        for (let excursion of excursions) {

          if (this.plan().includes("valletta") && excursion.plans.some((e) => e.includes("valletta")))
            this.offers["valletta"].push(excursion);
          else
            switch (true) {
              case excursion.short_title.toLowerCase().includes("gozo"):
                this.offers["gozo"].push(excursion);
                break;
              case excursion.short_title.toLowerCase().includes("comino"):
                this.offers["comino"].push(excursion);
                break;
              default:
                this.offers["malta"].push(excursion);
                break;
            }
        }

        const valletta = this.offers['valletta']?.length > 0;

        if(valletta){
          this.subtitle = "Here's a gift for you, take advantage of exclusive discounts with cloudigo while experiencing Valletta!";
          this.bannerTitle = "Save money on your adventures in Valletta";
          this.bannerImg = "old/tourist/valletta_image.webp";
          this.smBannerImg = "old/tourist/valletta_image.webp";
        }


      },
      error: (error) => {
        console.warn(error);
      },
    });
  }

  getFoodBrands() {
    this.http.get<IBrandCard[] | { brands: IBrandCard[] }>(backend + 'v3/brands/featured', {
      params: {
        filter: "5b3e190bbbe26a4ac441997f",
        sort: 'popularity',
        limit: 300,
        plans: this.plans.filter((p) => p != "global").join(',')
      }
    })
      .subscribe({
        next: (value) => {
          if('brands' in value){
            this.foodBrands = value.brands;
          } else {
            this.foodBrands = value;
          }
        },
        error: (error) => console.warn(error),
      });
  }

  ngOnInit(): void { }
}
